import { useEffect, useState } from 'react';
import styles from './Sidebar.module.scss';
import logo from '../../assets/images/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomNavItem } from 'components/CustomNavItem';
import { CustomButton } from 'components/CustomButton';
import { ContactUsModal } from 'components/ContactUsModal';
import { useActions, useAppSelector } from 'hooks';
import { doctorThunk } from 'state/thunks/doctorThunk/doctorThunk';
import {
  pendingPhotosCountSelect,
  pendingProvidersCountSelect,
} from 'state/selectors/doctorSelectors';
import { NAV_ITEMS, activeNavTitle } from './navItems';

export const Sidebar = () => {
  const [isContactModal, setIsContactModal] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getPendingProvidersCount, getPendingPhotosCount } = useActions(doctorThunk);
  const pendingCount = useAppSelector(pendingProvidersCountSelect);
  const pendingPhotoCount = useAppSelector(pendingPhotosCountSelect);

  /**
   * Which item is lit comes from the URL, not from the last click.
   *
   * See `activeNavTitle`: local state got this wrong on every reload and deep
   * link, and disagreed with the burger menu, which kept a second copy.
   */
  const selectedItem = activeNavTitle(pathname);

  /**
   * One badge for both provider queues.
   *
   * They are distinct decisions and the Providers page keeps them apart — an
   * account approval lets somebody start working, a photo approval only changes
   * what a patient sees — but they are worked from the SAME page, so two pills
   * on one nav item would be decoration rather than navigation. The badge
   * answers "is there anything waiting for me over there"; the page answers
   * what.
   */
  const waitingCount = pendingCount + pendingPhotoCount;

  /**
   * Fetched once when the authenticated shell mounts.
   *
   * The sidebar is the one component present on every admin page, which makes
   * it the natural home for a count that must be visible from anywhere. It is
   * not polled: approvals are a human-paced queue measured in minutes, and a
   * timer here would put a request on the wire every few seconds for the whole
   * time a console sits open on someone's second monitor. The count is
   * refreshed by the approval action itself and on a page load.
   */
  // Safe as a dependency: `useActions` memoises on the actions object, and
  // `doctorThunk` is a module-level constant, so this reference is stable and
  // the effect still runs exactly once.
  useEffect(() => {
    getPendingProvidersCount();
    getPendingPhotosCount();
  }, [getPendingProvidersCount, getPendingPhotosCount]);

  return (
    <nav className={styles.container} aria-label="Main">
      {/* Decorative here: the sidebar sits inside the authenticated shell, so
          the product is already established and the nav links carry the
          meaning. An alt would just be repeated on every page. */}
      <div className={styles.brand}>
        <img className={styles.logo} src={logo} alt="" />
      </div>

      {/* The only part of the sidebar that scrolls. The brand above and the
          button below are pinned, so a short window shortens the list rather
          than pushing its last items underneath the button. */}
      <div className={styles.itemsContainer}>
        {NAV_ITEMS.map(item => (
          <CustomNavItem
            key={item.title}
            onSelect={() => navigate(item.route)}
            selectedItem={selectedItem}
            title={item.title}
            icon={item.icon(selectedItem === item.title)}
            badgeCount={item.showsPendingBadge ? waitingCount : undefined}
          />
        ))}
      </div>

      {isContactModal && <ContactUsModal setContactModal={setIsContactModal} />}

      <div className={styles.buttonContainer}>
        <CustomButton
          isIcon
          title={'Contact us info'}
          handleClick={() => {
            setIsContactModal(true);
          }}
        />
      </div>
    </nav>
  );
};
